import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { ApiError } from './api/client';
import Ornament from './components/Ornament';
import Icon from './components/Icon';
import './Enroll.css';

const EMPTY = {
    headline: '', bio: '',
    expertise_areas: [], languages: [],
    vouches_for: ['candidate'], sectors: [],
    mediation_offered: false, mediation_languages: [], weekly_capacity_hours: '',
    default_fee_amount: '', default_fee_currency: 'USD', accepts_gratis: true,
    payment_method: 'off-platform',
    linkedin_url: '', public_profile_url: '',
    professional_history: [{ role: '', org: '', years: '' }],
    ref_contacts: [{ name: '', relation: '', contact: '' }],
    accepted_code_of_conduct: false, accepted_commission_terms: false,
};

const VOUCH_PILLS = ['candidate', 'job', 'organisation'];
const SECTOR_PILLS = [
    'Veda-adhyayana', 'Saṁskṛta', 'Āyurveda', 'Jyotiṣa', 'Yoga · Vedānta',
    'Temple administration', 'Pāṭhaśālā', 'Academia', 'Publishing', 'Media',
    'Software · Engineering', 'Healthcare', 'Law · Policy', 'Finance', 'Education',
];
const PAY_OPTIONS = [
    { value: 'razorpay',      label: 'Razorpay (India · INR)' },
    { value: 'stripe',        label: 'Stripe (USA · USD)' },
    { value: 'paypal',        label: 'PayPal (international · USD)' },
    { value: 'off-platform',  label: 'Off-platform (handled directly)' },
];

const EnrollReferee = () => {
    const [form, setForm] = useState(EMPTY);
    const [tagInput, setTagInput] = useState({ expertise_areas: '', languages: '', mediation_languages: '' });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (!localStorage.getItem('token')) {
            navigate('/signin?next=' + encodeURIComponent('/enroll/referee'));
            return;
        }
        api.get('/api/enroll/me')
            .then((data) => {
                if (data?.referee) {
                    setForm((prev) => ({
                        ...prev,
                        ...Object.fromEntries(
                            Object.entries(data.referee).filter(([k, v]) => v !== null && k in prev)
                        ),
                        expertise_areas: data.referee.expertise_areas || [],
                        languages: data.referee.languages || [],
                        mediation_languages: data.referee.mediation_languages || [],
                        vouches_for: data.referee.vouches_for || ['candidate'],
                        sectors: data.referee.sectors || [],
                        professional_history: data.referee.professional_history?.length
                            ? data.referee.professional_history : prev.professional_history,
                        ref_contacts: data.referee.ref_contacts?.length
                            ? data.referee.ref_contacts : prev.ref_contacts,
                    }));
                }
            })
            .catch(() => {});
    }, [navigate]);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const togglePill = (k, value) => setForm((p) => ({
        ...p,
        [k]: p[k].includes(value) ? p[k].filter((x) => x !== value) : [...p[k], value],
    }));
    const addTag = (k) => {
        const v = tagInput[k].trim();
        if (!v || form[k].includes(v)) { setTagInput({ ...tagInput, [k]: '' }); return; }
        set(k, [...form[k], v]);
        setTagInput({ ...tagInput, [k]: '' });
    };
    const removeTag = (k, value) => set(k, form[k].filter((x) => x !== value));

    const setHistory = (i, field, value) => {
        const next = [...form.professional_history];
        next[i] = { ...next[i], [field]: value };
        set('professional_history', next);
    };
    const addHistory = () => set('professional_history', [...form.professional_history, { role: '', org: '', years: '' }]);

    const setRefContact = (i, field, value) => {
        const next = [...form.ref_contacts];
        next[i] = { ...next[i], [field]: value };
        set('ref_contacts', next);
    };
    const addRefContact = () => set('ref_contacts', [...form.ref_contacts, { name: '', relation: '', contact: '' }]);

    const submit = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        if (!form.accepted_code_of_conduct) { setError('Please accept the Code of Conduct.'); return; }
        if (!form.accepted_commission_terms) { setError('Please acknowledge the 10% platform commission terms.'); return; }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                weekly_capacity_hours: form.weekly_capacity_hours ? parseInt(form.weekly_capacity_hours, 10) : null,
                default_fee_amount:    form.default_fee_amount ? parseFloat(form.default_fee_amount) : null,
            };
            await api.post('/api/enroll/referee', payload);
            setSuccess('Referee profile saved. Verification by our team typically takes 2–3 days; you can edit any time.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Failed to save. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="enroll-page">
            <div className="enroll-shell">

                <header className="enroll-header">
                    <Ornament variant="rule" tone="gold" size={120} style={{ margin: '0 auto 12px', display: 'block' }} />
                    <span className="enroll-eyebrow">Refer · Vouch · Mediate</span>
                    <h1>Enrol as a Referee</h1>
                    <p>
                        Vouch for a candidate's competence or for the integrity of an opportunity.
                        Charge a nominal fee, or do it gratis. Stay in the middle ground if you'd
                        like, talking to both sides.
                    </p>
                </header>

                <form className="enroll-form" onSubmit={submit}>

                    {/* 1. Profile */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">1</span>
                            <h2>Your profile</h2>
                        </div>
                        <div className="enroll-field">
                            <label>Headline <span className="hint">(one line)</span></label>
                            <input type="text" value={form.headline}
                                   onChange={(e) => set('headline', e.target.value)}
                                   placeholder="Vedic scholar, IIT alum, 30+ years in industry" />
                        </div>
                        <div className="enroll-field">
                            <label>Bio</label>
                            <textarea value={form.bio} onChange={(e) => set('bio', e.target.value)}
                                      placeholder="Background, paramparā, why you're suited to vouch — 2-3 paragraphs." />
                        </div>
                        <div className="enroll-field">
                            <label>Areas of expertise <span className="hint">(Enter to add)</span></label>
                            <div className="enroll-tag-input">
                                {form.expertise_areas.map((s) => (
                                    <span key={s} className="enroll-tag">
                                        {s}<button type="button" onClick={() => removeTag('expertise_areas', s)}>×</button>
                                    </span>
                                ))}
                                <input type="text" value={tagInput.expertise_areas}
                                       onChange={(e) => setTagInput({ ...tagInput, expertise_areas: e.target.value })}
                                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag('expertise_areas'); } }}
                                       placeholder={form.expertise_areas.length ? '' : 'Mīmāṁsā, Hiring, Software architecture…'} />
                            </div>
                        </div>
                        <div className="enroll-field">
                            <label>Languages</label>
                            <div className="enroll-tag-input">
                                {form.languages.map((s) => (
                                    <span key={s} className="enroll-tag">
                                        {s}<button type="button" onClick={() => removeTag('languages', s)}>×</button>
                                    </span>
                                ))}
                                <input type="text" value={tagInput.languages}
                                       onChange={(e) => setTagInput({ ...tagInput, languages: e.target.value })}
                                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag('languages'); } }} />
                            </div>
                        </div>
                    </div>

                    {/* 2. Vouching scope */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">2</span>
                            <h2>What &amp; whom you'll vouch for</h2>
                        </div>
                        <div className="enroll-field">
                            <label>Subjects you'll vouch for</label>
                            <div className="enroll-pill-group">
                                {VOUCH_PILLS.map((t) => (
                                    <button type="button" key={t}
                                            className={`enroll-pill ${form.vouches_for.includes(t) ? 'selected' : ''}`}
                                            onClick={() => togglePill('vouches_for', t)}>
                                        {t === 'candidate' ? 'Candidates' : t === 'job' ? 'Jobs' : 'Organisations'}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="enroll-field">
                            <label>Sectors</label>
                            <div className="enroll-pill-group">
                                {SECTOR_PILLS.map((t) => (
                                    <button type="button" key={t}
                                            className={`enroll-pill ${form.sectors.includes(t) ? 'selected' : ''}`}
                                            onClick={() => togglePill('sectors', t)}>
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* 3. Mediation */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">3</span>
                            <h2>Mediation <span className="hint">(stay in the middle ground)</span></h2>
                        </div>
                        <div className="enroll-checkbox-row">
                            <input type="checkbox" id="med" checked={form.mediation_offered}
                                   onChange={(e) => set('mediation_offered', e.target.checked)} />
                            <label htmlFor="med">
                                I'm willing to <strong>talk to both sides</strong> — speak to the candidate
                                and the employer separately, and broker the introduction.
                            </label>
                        </div>
                        {form.mediation_offered && (
                            <>
                                <div className="enroll-field">
                                    <label>Languages I can mediate in</label>
                                    <div className="enroll-tag-input">
                                        {form.mediation_languages.map((s) => (
                                            <span key={s} className="enroll-tag">
                                                {s}<button type="button" onClick={() => removeTag('mediation_languages', s)}>×</button>
                                            </span>
                                        ))}
                                        <input type="text" value={tagInput.mediation_languages}
                                               onChange={(e) => setTagInput({ ...tagInput, mediation_languages: e.target.value })}
                                               onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag('mediation_languages'); } }} />
                                    </div>
                                </div>
                                <div className="enroll-field">
                                    <label>Weekly capacity (hours)</label>
                                    <input type="number" min="0" max="80" value={form.weekly_capacity_hours}
                                           onChange={(e) => set('weekly_capacity_hours', e.target.value)} />
                                </div>
                            </>
                        )}
                    </div>

                    {/* 4. Compensation */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">4</span>
                            <h2>Compensation</h2>
                        </div>
                        <p className="enroll-section-hint">
                            You may charge a nominal fee, or refer pro bono. The platform retains 10% on paid introductions
                            (for charity-corpus upkeep). All payments route through{' '}
                            <strong>Dharma Poshanam</strong>:
                            <a href="https://dharmaposhanam.in/support" target="_blank" rel="noreferrer">/support</a>{' '}
                            (USA · Stripe / PayPal) ·{' '}
                            <a href="https://dharmaposhanam.in/donate"  target="_blank" rel="noreferrer">/donate</a>{' '}
                            (India · Razorpay). <em>Refunds are not issued — funds accrue to the charity corpus.</em>
                        </p>
                        <div className="enroll-row-3">
                            <div className="enroll-field">
                                <label>Default fee amount</label>
                                <input type="number" min="0" step="50" value={form.default_fee_amount}
                                       onChange={(e) => set('default_fee_amount', e.target.value)}
                                       placeholder="300" />
                            </div>
                            <div className="enroll-field">
                                <label>Currency</label>
                                <select value={form.default_fee_currency}
                                        onChange={(e) => set('default_fee_currency', e.target.value)}>
                                    <option value="USD">USD</option>
                                    <option value="INR">INR</option>
                                    <option value="GBP">GBP</option>
                                    <option value="EUR">EUR</option>
                                </select>
                            </div>
                            <div className="enroll-field">
                                <label>Payment method</label>
                                <select value={form.payment_method}
                                        onChange={(e) => set('payment_method', e.target.value)}>
                                    {PAY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="enroll-checkbox-row">
                            <input type="checkbox" id="gratis" checked={form.accepts_gratis}
                                   onChange={(e) => set('accepts_gratis', e.target.checked)} />
                            <label htmlFor="gratis">I'm also willing to refer <strong>gratis</strong> in select cases.</label>
                        </div>
                    </div>

                    {/* 5. Credentials */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">5</span>
                            <h2>Your credentials</h2>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>LinkedIn</label>
                                <input type="url" value={form.linkedin_url}
                                       onChange={(e) => set('linkedin_url', e.target.value)} />
                            </div>
                            <div className="enroll-field">
                                <label>Public profile / website</label>
                                <input type="url" value={form.public_profile_url}
                                       onChange={(e) => set('public_profile_url', e.target.value)} />
                            </div>
                        </div>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: 12.5, color: 'var(--sw-navy)',
                                        textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 8, marginTop: 12 }}>
                            Professional history
                        </label>
                        {form.professional_history.map((row, i) => (
                            <div className="enroll-row-3" key={i}>
                                <div className="enroll-field">
                                    <input type="text" placeholder="Role" value={row.role}
                                           onChange={(e) => setHistory(i, 'role', e.target.value)} />
                                </div>
                                <div className="enroll-field">
                                    <input type="text" placeholder="Organisation" value={row.org}
                                           onChange={(e) => setHistory(i, 'org', e.target.value)} />
                                </div>
                                <div className="enroll-field">
                                    <input type="text" placeholder="Years (e.g., 2010–2020)" value={row.years}
                                           onChange={(e) => setHistory(i, 'years', e.target.value)} />
                                </div>
                            </div>
                        ))}
                        <button type="button" className="sw-btn sw-btn-ghost sw-btn-sm" onClick={addHistory}>+ Add another</button>
                    </div>

                    {/* 6. Reference contacts */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">6</span>
                            <h2>Reference contacts <span className="hint">(at least one)</span></h2>
                        </div>
                        <p className="enroll-section-hint">
                            Two people we can reach to verify you. Their contact details are private to the platform team.
                        </p>
                        {form.ref_contacts.map((row, i) => (
                            <div className="enroll-row-3" key={i}>
                                <div className="enroll-field">
                                    <input type="text" placeholder="Name" value={row.name}
                                           onChange={(e) => setRefContact(i, 'name', e.target.value)} />
                                </div>
                                <div className="enroll-field">
                                    <input type="text" placeholder="Relation" value={row.relation}
                                           onChange={(e) => setRefContact(i, 'relation', e.target.value)} />
                                </div>
                                <div className="enroll-field">
                                    <input type="text" placeholder="Email or phone" value={row.contact}
                                           onChange={(e) => setRefContact(i, 'contact', e.target.value)} />
                                </div>
                            </div>
                        ))}
                        <button type="button" className="sw-btn sw-btn-ghost sw-btn-sm" onClick={addRefContact}>+ Add another</button>
                    </div>

                    {/* 7. Acknowledgements */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">7</span>
                            <h2>Acknowledgements</h2>
                        </div>
                        <div className="enroll-checkbox-row">
                            <input type="checkbox" id="coc" checked={form.accepted_code_of_conduct}
                                   onChange={(e) => set('accepted_code_of_conduct', e.target.checked)} required />
                            <label htmlFor="coc">
                                I have read and accept the Samudyoga <a href="#">Referee Code of Conduct</a>.
                            </label>
                        </div>
                        <div className="enroll-checkbox-row">
                            <input type="checkbox" id="commission" checked={form.accepted_commission_terms}
                                   onChange={(e) => set('accepted_commission_terms', e.target.checked)} required />
                            <label htmlFor="commission">
                                I acknowledge the platform retains <strong>10% commission</strong> on paid introductions and may
                                use Stripe / PayPal for fee collection.
                            </label>
                        </div>
                    </div>

                    {success && <div className="enroll-success"><Icon name="check" size={16} /> {success}</div>}
                    {error   && <div className="enroll-error">{error}</div>}

                    <div className="enroll-submit-row">
                        <span className="left">After submission, our team verifies references in 2–3 days.</span>
                        <div className="right">
                            <button type="button" className="sw-btn sw-btn-ghost" onClick={() => navigate('/refer')}>
                                Cancel
                            </button>
                            <button type="submit" className="sw-btn sw-btn-saffron sw-btn-lg" disabled={submitting}>
                                {submitting ? 'Saving…' : 'Submit referee profile'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EnrollReferee;
